
import { SanityService } from "@modules/common/cms/sanityService"
import { SanityContentBlock } from "@modules/common/cms/sanityType"
import { CrossFitEventDto } from "@modules/crossfitEvent/api/crossfitEventDto"
import dayjs from "dayjs"

type CrossFitEventState = Omit<CrossFitEvent, "getWatchLiveLinks">
export class CrossFitEvent {
  id: string
  title: string
  subtitle: string
  place: string
  card: {
    title: string
    subtitle: string
  }
  description?: SanityContentBlock
  date: {
    start: string
    end: string
  }
  prod?: {
    box_id: string
    offer_id: string
    default_guest?: string
  }
  demo?: {
    box_id: string
    offer_id: string
  }
  dev?: {
    box_id: string
    offer_id: string
  }
  plan?: {
    uri: string
    cache: boolean
    expiration?: number
  }
  crossfitLogo?: {
    uri: string
  }
  logoCircle?: {
    uri: string
  }
  logo?: {
    uri: string
  }
  background?: {
    DARK: {
      uri: string
    }
    LIGHT: {
      uri: string
    }
  }
  leaderboards?: {
    url: string
    title: string
  }[]
  schedules?: {
    url: string
    title: string
  }[]
  watchLive?: {
    url: string
    title: string
    date?: string
  }[]
  cta?: {
    tickets?: string
    website?: string
  }
  badgeCode?: string
  coords?: {
    lat: number
    lng: number
  }

  private constructor(event: CrossFitEventState) {
    this.id = event.id
    this.title = event.title
    this.subtitle = event.subtitle
    this.place = event.place
    this.card = event.card
    this.description = event.description
    this.date = event.date
    this.prod = event.prod
    this.demo = event.demo
    this.dev = event.dev
    this.plan = event.plan
    this.crossfitLogo = event.crossfitLogo
    this.logoCircle = event.logoCircle
    this.logo = event.logo
    this.background = event.background
    this.leaderboards = event.leaderboards
    this.schedules = event.schedules
    this.watchLive = event.watchLive
    this.cta = event.cta
    this.badgeCode = event.badgeCode
    this.coords = event.coords
  }

  static fromState(event: CrossFitEventState): CrossFitEvent {
    return new CrossFitEvent(event)
  }

  static fromDto(eventDto: CrossFitEventDto): CrossFitEvent {
    const event = new CrossFitEvent({
      id: eventDto.id.current,
      title: eventDto.title,
      subtitle: eventDto.subtitle || "",
      place: eventDto.place || "",
      card: {
        title: eventDto.card?.title || "",
        subtitle: eventDto.card?.subtitle || "",
      },
      description: SanityService.buildContentBlock(eventDto.description),
      date: eventDto.date,
      prod: eventDto.prod,
      demo: eventDto.demo,
      dev: eventDto.dev,
      plan: eventDto.plan,
      crossfitLogo: eventDto.crossfit_logo && {
        uri: SanityService.buildImageUri(eventDto.crossfit_logo),
      },
      logoCircle: eventDto.logo_circle && {
        uri: SanityService.buildImageUri(eventDto.logo_circle),
      },
      logo: eventDto.logo && {
        uri: SanityService.buildImageUri(eventDto.logo),
      },
      background: eventDto.background && {
        DARK: { uri: SanityService.buildImageUri(eventDto.background.DARK) },
        LIGHT: { uri: SanityService.buildImageUri(eventDto.background.LIGHT) },
      },
      leaderboards: eventDto.leaderboards,
      schedules: eventDto.schedules,
      watchLive: eventDto.watch_live,
      cta: eventDto.cta,
      badgeCode: eventDto.badge_code,
      coords: eventDto.coords,
    })

    return event
  }

  getWatchLiveLinks():
    | {
        url: string
        title: string
        date?: string
      }[]
    | undefined {
    const availableLinks = this.watchLive?.filter((live) => {
      if (!live.date) {
        return true
      }

      return live.date && dayjs().isSame(live.date, "day")
    })

    return availableLinks
  }
}
